import React from 'react';
import {
  FREE_TIER,
  useCreateOrganization,
  useCreatePersonalOrganization,
  useCurrentOrgContext,
} from '@redbax/shared';
import { SoloSpider, SpiderGroup } from '@redbax/ui-kit';
import { OnboardingShell } from './OnboardingShell';

// Only uk-south is provisionable today — every tenant database is created on a single
// Cloud SQL instance in europe-west2 (London). The others are shown so the roadmap is
// visible, but stay disabled until per-region infrastructure lands (epic #90). The
// domain rejects unavailable regions too, so this is not the only guard.
const REGIONS = [
  { value: 'uk-south', label: 'UK South (London)', available: true },
  { value: 'west-europe', label: 'West Europe — coming soon', available: false },
  { value: 'east-us', label: 'East US — coming soon', available: false },
];

type Choice = 'individual' | 'company';

/**
 * How someone says which kind of account they want (epic #121, story #129).
 *
 * THE CHOICE HAS TO BE ASKED. Story #129 specified creating a personal organization automatically "on
 * first sign-in with no membership", but that signal cannot tell the two cases apart: a company founder
 * signing up for the first time also has no membership, and would have been silently given a personal
 * organization named after themselves. So the first screen asks, and only then is anything created.
 *
 * Nothing is created until a card is chosen, which is also why this is one screen rather than a wizard:
 * the individual path has nothing left to ask for. The platform derives the name from the person's own
 * registered display name and forces the only provisionable region, so there is no form to fill in.
 */
export function OnboardingPage() {
  const createOrg = useCreateOrganization();
  const createPersonalOrg = useCreatePersonalOrganization();
  const { setCurrentOrg } = useCurrentOrgContext();

  const [choice, setChoice] = React.useState<Choice | null>(null);
  const [name, setName] = React.useState('');
  const [region, setRegion] = React.useState('uk-south');
  const [error, setError] = React.useState<string | null>(null);

  /**
   * A hard navigation rather than a client-side route change, in both paths. It sends the app back
   * through IdentityGate, which re-reads the membership and the plan from the server — so which shell
   * renders, which nav items exist and what the device cap is are all derived from the real row rather
   * than from what this screen happened to put in context.
   *
   * An individual goes to the guided first run rather than the dashboard. A dashboard with no device
   * is four zeroes and four empty charts, and it is the point where this journey previously stopped
   * dead; a company goes straight in, because an administrator setting up a team is not the person
   * this flow is written for.
   */
  const enterApp = (orgId: string, destination = '/') => {
    localStorage.setItem('redbax_current_org', orgId);
    window.location.href = destination;
  };

  const handleIndividual = async () => {
    setError(null);
    try {
      const result = await createPersonalOrg.mutateAsync();
      // A personal organization is Active the moment it is created — it joins a database that already
      // exists, so there is nothing to provision and nobody to approve it (ADR-014). That is why this
      // goes straight to the dashboard rather than the awaiting-approval screen.
      setCurrentOrg({
        orgId: result.id,
        name: '',
        role: 'Owner',
        status: 'Active',
        isPersonal: true,
      });
      enterApp(result.id, '/welcome');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create your account. Please try again.',
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Organization name is required.');
      return;
    }

    try {
      const result = await createOrg.mutateAsync({ name: name.trim(), region });
      setCurrentOrg({ orgId: result.id, name: name.trim(), role: 'Owner' });
      enterApp(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization.');
    }
  };

  if (choice === null) {
    return (
      <OnboardingShell title="How will you use Redbax?" subtitle="You can change this later." wide>
        <div className="grid gap-4 sm:grid-cols-2">
          <ChoiceCard
            heading="Just me"
            price="Free"
            art={<SoloSpider />}
            onClick={handleIndividual}
            busy={createPersonalOrg.isPending}
            busyLabel="Setting up your account…"
            points={[
              `${FREE_TIER.deviceLimit} devices`,
              `${FREE_TIER.retentionDays} days of event history`,
              'Redbax default detection policy',
              'Start straight away — no approval',
            ]}
          />
          <ChoiceCard
            heading="My company"
            price="Team plan"
            art={<SpiderGroup />}
            mirrored
            onClick={() => setChoice('company')}
            points={[
              'Unlimited devices',
              'Full event history',
              'Your own policies and exclusions',
              'Invite your colleagues',
            ]}
          />
        </div>

        <p className="mt-6 text-xs text-text-secondary">
          Working on your own to start with? Choose <strong>Just me</strong> — you can upgrade to a
          team whenever you like, and your devices keep working throughout.
        </p>

        {error && (
          <p className="mt-4 text-sm text-red-accent" role="alert">
            {error}
          </p>
        )}
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell
      title="Create your organization"
      subtitle="Set up your company to start protecting your AI endpoints."
      onBack={() => {
        setChoice(null);
        setError(null);
      }}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="org-name" className="mb-1 block text-sm font-medium text-text-primary">
            Organization name
          </label>
          <input
            id="org-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:border-red-accent focus:outline-none focus:ring-1 focus:ring-red-accent"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="org-region" className="mb-1 block text-sm font-medium text-text-primary">
            Data region
          </label>
          <select
            id="org-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            aria-describedby="org-region-help"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary focus:border-red-accent focus:outline-none focus:ring-1 focus:ring-red-accent"
          >
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value} disabled={!r.available}>
                {r.label}
              </option>
            ))}
          </select>
          <p id="org-region-help" className="mt-1 text-xs text-text-secondary">
            Your organization&rsquo;s data is stored in the UK. Additional regions are on
            the roadmap.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-accent" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={createOrg.isPending}
          className="w-full rounded-lg bg-red-accent px-4 py-2 text-sm font-medium text-text-primary hover:bg-red-hover disabled:opacity-50"
        >
          {createOrg.isPending ? 'Creating...' : 'Create organization'}
        </button>
      </form>
    </OnboardingShell>
  );
}

function ChoiceCard({
  heading,
  price,
  points,
  art,
  onClick,
  busy = false,
  busyLabel,
  mirrored = false,
}: {
  heading: string;
  price: string;
  points: string[];
  art: React.ReactNode;
  onClick: () => void;
  busy?: boolean;
  busyLabel?: string;
  /** Flips the corner web, so a pair of cards faces inward instead of repeating one image. */
  mirrored?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      // `group` is what drives the artwork: the spiders' legs and the dropline react to a hover anywhere
      // on the card, so the whole card feels like one target rather than the picture being a separate
      // hover zone from the text.
      className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-background p-5 text-left transition-all duration-300 hover:border-red-accent hover:shadow-[0_0_0_1px_var(--color-red-accent),0_12px_32px_-16px_var(--color-red-accent)] focus:border-red-accent focus:outline-none focus:ring-1 focus:ring-red-accent disabled:opacity-60"
    >
      {/* A web in the corner each card's spiders sit against. Mirrored on the second card so the two
          read as a facing pair rather than the same image twice. */}
      <img
        src="/web.svg"
        alt=""
        aria-hidden="true"
        className={`pointer-events-none absolute -top-16 h-56 w-56 select-none opacity-[0.10] transition-opacity duration-500 group-hover:opacity-[0.20] ${
          mirrored ? '-left-16 -scale-x-100 rotate-90' : '-right-16 rotate-90'
        }`}
      />

      {/* A FIXED height, shared by both cards. The artwork differs wildly in mass between one spider and
          five, and letting each card size to its own art made the pair look misaligned — which reads as
          one option being the recommended one. */}
      <div className="relative flex h-[148px] items-center justify-center">{art}</div>

      <span className="relative mt-1 text-base font-medium text-text-primary">{heading}</span>
      <span className="relative mt-0.5 text-xs uppercase tracking-wide text-red-accent">{price}</span>
      <ul className="relative mt-3 space-y-1.5">
        {points.map((point) => (
          <li key={point} className="flex items-start gap-2 text-sm text-text-secondary">
            <span
              aria-hidden="true"
              className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-red-accent/70"
            />
            {point}
          </li>
        ))}
      </ul>
      {busy && busyLabel && (
        <span className="relative mt-4 text-sm text-text-secondary">{busyLabel}</span>
      )}
    </button>
  );
}
